import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Heart, TrendingUp, Smartphone, Shield } from 'lucide-react';

const reasons = [
  {
    icon: Brain,
    title: 'Intelligent',
    description: 'AI-powered workflows and decision support to automate repetitive tasks.',
  },
  {
    icon: Heart,
    title: 'People-Centric',
    description: 'Built for employees, not just HR teams. Intuitive experience for everyone.',
  },
  {
    icon: TrendingUp,
    title: 'Scalable',
    description: 'From 10 to 10,000+ employees. Grows with your organization seamlessly.',
  },
  {
    icon: Smartphone,
    title: 'Mobile-First',
    description: 'Access HR from anywhere, anytime. Works on any device, any browser.',
  },
  {
    icon: Shield,
    title: 'Secure & Compliant',
    description: 'Role-based access controls and audit trails for complete compliance.',
  }
];

const WhyAurborBloom = () => {
  return (
    <section className="py-24 bg-brand-black text-white overflow-hidden relative">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-brand-accent font-semibold text-sm uppercase tracking-wider">
            Why Choose Us
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold">
            Legacy HR software is outdated
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            Traditional systems are disconnected and built for administrators — not for people. 
            We are changing that with a modern approach to HR management.
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
            >
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 h-full border border-white/10 hover:border-brand-accent/50 transition-all hover:bg-white/10">
                <div className="w-12 h-12 rounded-xl bg-brand-accent/20 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-brand-accent/30 transition-all">
                  <reason.icon className="w-6 h-6 text-brand-accent" />
                </div>
                <h3 className="text-lg font-bold mb-2">{reason.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{reason.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyAurborBloom;
